"use client";

import { NEXT_PUBLIC_WHATSAPP_PHONE } from "@guru/shared/config/env.client";
import { useTranslation } from "@guru/shared/i18n";
import { useUIStore } from "@guru/shared/stores";
import { cn } from "@guru/shared/utils/cn";
import { X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { WhatsAppIcon } from "./whatsapp-icon";

function TimeStamp() {
  const [time, setTime] = useState<string | null>(null);

  useEffect(() => {
    setTime(new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }));
  }, []);

  if (!time) return null;

  return <span className="mt-1 block text-right text-[10px] text-[#999]">{time}</span>;
}

export function WhatsAppWidget() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Don't render if no phone number configured
  if (!NEXT_PUBLIC_WHATSAPP_PHONE) return null;

  const defaultMessage =
    t.contact?.whatsappDefaultMessage ?? "Hi! I'd like to know more about EtheraGuru.";
  const headerText = t.contact?.widgetHeader ?? "Chat with us";
  const bodyText =
    t.contact?.widgetBody ??
    "Hey there! How can we help you today? Tap below to start a WhatsApp conversation.";
  const tooltip = t.contact?.widgetTooltip ?? "Chat on WhatsApp";
  const agentName = t.contact?.whatsappAgentName ?? "EtheraGuru Support";
  const startChatCta = t.contact?.startChatCta ?? "Start Chat";

  const whatsappUrl = `https://wa.me/${NEXT_PUBLIC_WHATSAPP_PHONE}?text=${encodeURIComponent(defaultMessage)}`;

  return (
    <WhatsAppWidgetInner
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      popupRef={popupRef}
      buttonRef={buttonRef}
      headerText={headerText}
      bodyText={bodyText}
      agentName={agentName}
      tooltip={tooltip}
      whatsappUrl={whatsappUrl}
      startChatCta={startChatCta}
    />
  );
}

/** Inner component that handles event listeners (hooks must not be called after early return) */
function WhatsAppWidgetInner({
  isOpen,
  setIsOpen,
  popupRef,
  buttonRef,
  headerText,
  bodyText,
  agentName,
  tooltip,
  whatsappUrl,
  startChatCta,
}: {
  isOpen: boolean;
  setIsOpen: (v: boolean | ((prev: boolean) => boolean)) => void;
  popupRef: React.RefObject<HTMLDivElement | null>;
  buttonRef: React.RefObject<HTMLButtonElement | null>;
  headerText: string;
  bodyText: string;
  agentName: string;
  tooltip: string;
  whatsappUrl: string;
  startChatCta: string;
}) {
  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(e: MouseEvent) {
      if (
        popupRef.current &&
        !popupRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, popupRef, buttonRef, setIsOpen]);

  useEffect(() => {
    if (!isOpen) return;
    function handleEscape(e: KeyboardEvent) {
      if (e.key === "Escape") setIsOpen(false);
    }
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, setIsOpen]);

  const isPwaBannerVisible = useUIStore((s) => s.isPwaBannerVisible);

  return (
    <div
      className={cn(
        "pointer-events-none fixed right-6 z-50 transition-[bottom] duration-300",
        isPwaBannerVisible ? "bottom-20" : "bottom-6"
      )}
      style={{ maxWidth: "360px" }}
    >
      {/* Popup */}
      <div
        ref={popupRef}
        className={cn(
          "mb-4 overflow-hidden rounded-2xl shadow-2xl transition-all duration-200 origin-bottom-right",
          isOpen
            ? "pointer-events-auto scale-100 opacity-100"
            : "pointer-events-none scale-95 opacity-0"
        )}
        role="dialog"
        aria-label="WhatsApp chat"
        aria-hidden={!isOpen}
      >
        {/* Header */}
        <div className="flex items-center gap-3 bg-[#075E54] px-5 py-4 text-white">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20 text-sm font-semibold text-white">
            {agentName.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1">
            <p className={cn("text-sm font-semibold leading-tight text-white")}>{headerText}</p>
            <p className="text-xs text-white/70">{agentName}</p>
          </div>
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            className="rounded-full p-1 text-white/70 transition-colors hover:bg-white/10 hover:text-white"
            aria-label="Close chat"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="bg-[#ECE5DD] px-5 py-5">
          <div className="max-w-[260px] rounded-lg rounded-tl-none bg-white px-4 py-3 shadow-sm">
            <p className={cn("text-sm leading-relaxed text-[#303030]")}>{bodyText}</p>
            <TimeStamp />
          </div>
        </div>

        {/* CTA */}
        <div className="bg-[#F0F0F0] px-5 py-3">
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#25D366] px-4 py-2.5 text-sm font-semibold text-white shadow transition-colors hover:bg-[#1EBE5A]"
          >
            <WhatsAppIcon className="h-5 w-5" />
            {startChatCta}
          </a>
        </div>
      </div>

      {/* Floating Action Button */}
      <div className="flex justify-end">
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setIsOpen((prev) => !prev)}
          className="pointer-events-auto group relative flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] shadow-lg transition-all duration-200 hover:scale-105 hover:shadow-xl active:scale-95"
          aria-label={tooltip}
          title={tooltip}
        >
          {isOpen ? (
            <X className="h-6 w-6 text-white" />
          ) : (
            <WhatsAppIcon className="h-7 w-7 text-white" />
          )}
        </button>
      </div>
    </div>
  );
}
